import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { CinematicText, VelocityScroll } from "./shared/CinematicProductLayout";
import ParticleNetwork from "./ParticleNetwork";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  Lock,
  Cpu,
  Zap,
  Activity,
  Fingerprint,
  Layers,
  CheckCircle2,
  Radio,
  Sliders,
  Scale,
  FlaskConical,
  Compass,
  BookOpen
} from "lucide-react";

// Multilingual prompts — 9 languages including Tamil and Hindi
const ANIMATED_PROMPTS = [
  "What is the future of AI?",                    // English
  "செயற்கை நுண்ணறிவின் எதிர்காலம் என்ன?",          // Tamil
  "कृत्रिम बुद्धिमत्ता का भविष्य क्या है?",             // Hindi
  "AIの未来はどうなりますか？",                         // Japanese
  "¿Cuál es el futuro de la inteligencia artificial?", // Spanish
  "ما هو مستقبل الذكاء الاصطناعي؟",                  // Arabic
  "Quel est l'avenir de l'intelligence artificielle ?", // French
  "인공지능의 미래는 어떻게 될까요?",                      // Korean
  "Wie sieht die Zukunft der künstlichen Intelligenz aus?", // German
];

const MainContent = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(null);
  const inputRef = useRef(null);

  // Particle scroll animation refs and transforms
  const particleSectionRef = useRef(null);
  const { scrollYProgress: particleScrollYProgress } = useScroll({
    target: particleSectionRef,
    offset: ["start start", "end end"]
  });

  const particleTextScale = useTransform(particleScrollYProgress, [0, 0.9], [1, window.innerWidth > 768 ? 5 : 3]);
  const particleTextOpacity = useTransform(particleScrollYProgress, [0, 0.6, 0.95], [1, 1, 0]);

  // Animated placeholder state
  const [animatedText, setAnimatedText] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [userHasTyped, setUserHasTyped] = useState(false);
  const animationRef = useRef(null);
  const timeoutRef = useRef(null);
  const [isSectionActive, setIsSectionActive] = useState(false);

  // Monitor scroll to activate particle effect
  useEffect(() => {
    const unsubscribe = particleScrollYProgress.onChange(v => {
      setIsSectionActive(v > 0.05 && v < 0.95);
    });
    return () => unsubscribe();
  }, [particleScrollYProgress]);

  // Typewriter animation engine
  const runTypewriterCycle = useCallback(() => {
    if (userHasTyped) return;

    let promptIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let currentText = "";
    setIsAnimating(true);

    const tick = () => {
      if (userHasTyped) {
        setAnimatedText("");
        setIsAnimating(false);
        return;
      }

      const currentPrompt = ANIMATED_PROMPTS[promptIndex];

      if (!isDeleting) {
        // Typing forward
        charIndex++;
        currentText = currentPrompt.slice(0, charIndex);
        setAnimatedText(currentText);

        if (charIndex >= currentPrompt.length) {
          // Finished typing — pause before deleting
          animationRef.current = setTimeout(() => {
            isDeleting = true;
            tick();
          }, 2000);
          return;
        }
        // Typing speed: slightly randomized for natural feel
        animationRef.current = setTimeout(tick, 40 + Math.random() * 30);
      } else {
        // Deleting
        charIndex--;
        currentText = currentPrompt.slice(0, charIndex);
        setAnimatedText(currentText);

        if (charIndex <= 0) {
          // Finished deleting — move to next prompt
          isDeleting = false;
          promptIndex = (promptIndex + 1) % ANIMATED_PROMPTS.length;
          animationRef.current = setTimeout(tick, 400);
          return;
        }
        // Deleting speed: faster than typing
        animationRef.current = setTimeout(tick, 20 + Math.random() * 15);
      }
    };

    tick();
  }, [userHasTyped]);

  // Start animation after 5 seconds
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (!userHasTyped) {
        runTypewriterCycle();
      }
    }, 5000);

    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(animationRef.current);
    };
  }, [runTypewriterCycle, userHasTyped]);

  // Stop animation when user types
  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    if (!userHasTyped && val.length > 0) {
      setUserHasTyped(true);
      setAnimatedText("");
      setIsAnimating(false);
      clearTimeout(animationRef.current);
      clearTimeout(timeoutRef.current);
    }
    // If user clears input, allow animation to restart
    if (val.length === 0) {
      setUserHasTyped(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const query = searchValue.trim();
    if (!query) return;
    
    window.open(`https://taels.fozzil.in/?q=${encodeURIComponent(query)}`, "_blank", "noopener,noreferrer");
    setSearchValue("");
    setUserHasTyped(false);
  };

  const bentoProducts = [
    { 
      id: "ark", 
      name: "FOZZIL ARK", 
      tagline: "The Evolution of the Exhibit.",
      category: "Spatial Intelligence", 
      desc: "Transform physical spaces into ambient, reactive neural environments with ultra-low latency spatial projection.", 
      path: "/fozzil-ark",
      badge: "Flagship",
      accent: "from-blue-500/20 via-sky-500/10 to-transparent",
      glow: "rgba(59, 130, 246, 0.25)"
    },
    { 
      id: "medos", 
      name: "MEDOS", 
      tagline: "1st AI Hospital Pharmacy Management OS",
      category: "Healthcare OS", 
      desc: "Autonomous clinical pharmacy management operating system eliminating medication dispensing errors with deterministic validation.", 
      path: "/medos",
      badge: "Clinical Grade",
      accent: "from-blue-500/20 via-sky-500/10 to-transparent",
      glow: "rgba(59, 130, 246, 0.25)"
    },
    { 
      id: "zypher", 
      name: "ZYPHER AI", 
      tagline: "Neuro-Clinical Advisory Engine",
      category: "Diagnostic AI", 
      desc: "High-confidence clinical advisory and biomedical reasoning engine with transparent, explainable decision boundaries.", 
      path: "/zypherai",
      badge: "Autonomous",
      accent: "from-blue-500/20 via-sky-500/10 to-transparent",
      glow: "rgba(59, 130, 246, 0.25)"
    },
    { 
      id: "store", 
      name: "FOZZIL Store", 
      tagline: "Hardware, Nodes & Subscriptions",
      category: "Ecosystem Provisioning", 
      desc: "Deploy proprietary FOZZIL hardware terminals, edge computing nodes, and enterprise intelligence subscriptions.", 
      path: "/shop",
      badge: "Provisioning",
      accent: "from-blue-500/20 via-sky-500/10 to-transparent",
      glow: "rgba(59, 130, 246, 0.25)"
    }
  ];

  const products = bentoProducts;

  // Determine what to show as placeholder
  const showAnimatedPlaceholder = isAnimating && !userHasTyped && !searchValue;

  return (
    <div className="bg-black text-white selection:bg-blue-500/30 overflow-x-hidden">
      {/* 1. ORIGINAL HERO (Reverted) */}
      <section
        ref={heroRef}
        className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
      >
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black to-blue-600/[0.10]"></div>
          <div className="absolute inset-0 hidden md:block">
            <div className="absolute top-1/4 right-1/4 w-[45vw] h-[45vw] bg-blue-600/[0.05] rounded-full blur-[140px]"></div>
            <div className="absolute bottom-1/4 right-1/3 w-[40vw] h-[40vw] bg-blue-500/[0.04] rounded-full blur-[120px]"></div>
            <div className="absolute top-1/3 right-1/4 w-[35vw] h-[35vw] bg-blue-600/[0.04] rounded-full blur-[150px]"></div>
            <div className="absolute bottom-1/2 right-1/4 w-[50vw] h-[50vw] bg-blue-500/[0.03] rounded-full blur-[140px]"></div>
          </div>
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/50 to-black/80"></div>
        </div>

        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <h1 className="text-[clamp(1.5rem,4vw,2.8rem)] font-medium text-white leading-tight">
              Intelligence At Your Command!
            </h1>
            
            <form onSubmit={handleSearch} className="max-w-3xl mx-auto mt-12">
              <div 
                className={`relative w-full bg-[#0a0a0a]/80 border border-blue-500/25 rounded-[32px] transition-all duration-200 ${
                  inputFocused ? "ring-1 ring-blue-500/30" : ""
                }`}
              >
                {/* Animated typewriter placeholder — aligned top-left */}
                {showAnimatedPlaceholder && (
                  <div
                    className="absolute left-6 top-5 md:top-7 pointer-events-none z-10"
                  >
                    <span className="text-lg md:text-xl text-gray-500 select-none">
                      {animatedText}
                      <span className="inline-block w-[2px] h-[1.2em] bg-blue-400/70 ml-[1px] align-middle animate-pulse" />
                    </span>
                  </div>
                )}
                
                {/* Scrollable text container */}
                <div className="px-6 pt-5 pb-5 md:pt-7 md:pb-7 min-h-[120px] md:min-h-[140px] flex">
                  <textarea
                    ref={inputRef}
                    value={searchValue}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (searchValue.trim()) handleSearch(e);
                      }
                    }}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    placeholder={showAnimatedPlaceholder ? "" : "Command Taels..."}
                    className="w-full pr-16 bg-transparent text-white placeholder-gray-500 focus:outline-none text-lg md:text-xl resize-none overflow-y-auto [&::-webkit-scrollbar]:hidden"
                    style={{ height: "80px", minHeight: "80px", maxHeight: "200px" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={!searchValue.trim()}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 group transition-opacity duration-300 ${
                    !searchValue.trim() ? "opacity-30 cursor-not-allowed" : "opacity-100"
                  }`}
                >
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full bg-white flex items-center justify-center transition-all duration-300 ${
                    searchValue.trim() ? "hover:bg-gray-200 active:scale-95" : ""
                  }`}>
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5M5 12l7-7 7 7"/>
                    </svg>
                  </div>
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* 2. PARTICLE NETWORK SECTION (Sticky Scroll Scale Effect) */}
      <section ref={particleSectionRef} className="h-[140vh] md:h-[150vh] relative bg-black">
        <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden will-change-transform">
          <ParticleNetwork isSectionActive={isSectionActive} />
          
          <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-12 pointer-events-none">
            <motion.div 
              style={{
                scale: particleTextScale,
                opacity: particleTextOpacity,
                willChange: "transform, opacity"
              }}
              className="flex flex-col w-full origin-center"
            >
              <h2 className="text-[clamp(2rem,5vw,5rem)] font-normal text-white/90 leading-none tracking-tight">
                Connect
              </h2>
              <h2 className="text-[clamp(2rem,5vw,5rem)] font-normal text-white/90 leading-none tracking-tight text-right mt-12 md:mt-32">
                The Future
              </h2>
            </motion.div>
          </div>
        </div>
      </section>


      {/* 3. THE FOZZIL ECOSYSTEM (Apple-Style Architecture of Intelligence) */}
      <section className="relative py-28 md:py-48 px-6 bg-black overflow-hidden border-t border-white/[0.06]">
        {/* Apple-style atmospheric backlight */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(6,93,192,0.18),transparent_70%)] blur-3xl" />
          <div className="absolute top-[40%] right-[-10%] w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(59,130,246,0.06)_0%,transparent_70%)] blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-4xl mx-auto space-y-6 mb-16 md:mb-24">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-semibold tracking-[-0.04em] leading-[0.95] text-white"
            >
              The Architecture <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-indigo-300">
                of Intelligence.
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-lg sm:text-2xl text-zinc-400 font-normal leading-relaxed max-w-3xl mx-auto"
            >
              A vertically integrated network engineered from silicon to model. Harmonizing autonomous perception, clinical precision, and absolute data privacy across every device.
            </motion.p>
          </div>

          {/* Flagship Hero Card: T.A.E.L.S APEX */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -4, transition: { duration: 0.4 } }}
            className="relative rounded-[32px] sm:rounded-[44px] bg-gradient-to-b from-white/[0.08] to-white/[0.02] border border-white/[0.1] backdrop-blur-3xl p-8 sm:p-14 overflow-hidden group shadow-[0_30px_100px_rgba(0,0,0,0.8)] before:absolute before:inset-x-0 before:top-0 before:h-[1px] before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent"
          >
            {/* Ambient internal card glow */}
            <div className="absolute top-0 right-1/4 w-[450px] h-[350px] bg-blue-500/10 blur-[110px] rounded-full pointer-events-none group-hover:bg-blue-500/15 transition-all duration-700" />

            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
              <div className="space-y-6 max-w-2xl">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white group-hover:text-blue-400 transition-colors duration-500">
                      T.A.E.L.S
                    </h3>            
                  </div>
                  <p className="text-base sm:text-xl text-zinc-300 font-light leading-relaxed">
                    The autonomous multi-modal intelligence foundation. Orchestrating complex clinical decision-making, natural language synthesis, and real-time enterprise reasoning without compromising user privacy.
                  </p>
                </div>

                {/* Apple Action CTAs */}
                <div className="flex flex-wrap items-center gap-5 pt-4">
                  <button
                    onClick={() => window.open("https://taels.fozzil.in/app", "_blank", "noopener,noreferrer")}
                    className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition-all shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
                  >
                    <span>Launch T.A.E.L.S</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate("/pricing-taels")}
                    className="text-sm sm:text-base text-[#2997ff] hover:text-sky-300 transition-colors inline-flex items-center gap-1.5 font-medium group cursor-pointer"
                  >
                    <span>View T.A.E.L.S Plans</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>

              {/* Visual Core Graphic */}
              <div className="relative w-full lg:w-80 h-72 lg:h-80 flex items-center justify-center shrink-0">
                <div className="relative w-56 h-56 sm:w-64 sm:h-64 lg:w-72 lg:h-72 flex items-center justify-center">
                  {/* Subtle Ambient Back Glow */}
                  <div className="absolute inset-4 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

                  {/* Enlarged TAELS Logo */}
                  <div className="relative w-full h-full rounded-full overflow-hidden flex items-center justify-center shadow-[0_0_50px_rgba(6,93,192,0.3)] bg-black">
                    <img
                      src="/TAELS Logo black bg.png"
                      alt="T.A.E.L.S"
                      className="w-full h-full object-cover rounded-full select-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* 4. PRODUCT SHOWCASE BENTO GRID (Apple-Style Interactive Bento) */}
      <section className="py-24 md:py-36 px-6 bg-black">
        <div className="max-w-7xl mx-auto space-y-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-white/[0.08]">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-[#38bdf8]">
                <span>Ecosystem Suite</span>
              </div>
              <h3 className="text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white">
                Engineered for Every Domain.
              </h3>
            </div>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-6 lg:gap-8">
            {/* Bento 1: FOZZIL ARK (Hero Featured Card - Spans 2 Cols) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              onClick={() => navigate("/fozzil-ark")}
              className="col-span-2 lg:col-span-2 relative rounded-[24px] sm:rounded-[40px] bg-gradient-to-b from-white/[0.06] to-white/[0.015] border border-white/[0.08] hover:border-blue-500/30 p-5 sm:p-10 md:p-12 overflow-hidden group cursor-pointer transition-colors duration-500 flex flex-col justify-between min-h-[300px] sm:min-h-[440px]"
            >
              {/* Ambient backdrop glow */}
              <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-500/10 blur-[100px] rounded-full pointer-events-none group-hover:bg-blue-500/20 transition-all duration-700" />

              <div className="relative z-10 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-mono uppercase tracking-widest text-blue-400">
                    Spatial Intelligence
                  </span>
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
                    FI Exhibit
                  </span>
                </div>
                <h4 className="text-2xl sm:text-5xl font-semibold tracking-tight text-white group-hover:text-blue-400 transition-colors">
                  FOZZIL ARK
                </h4>
                <p className="text-sm sm:text-lg text-zinc-300 font-light leading-relaxed max-w-xl">
                  The evolution of the exhibit. Ambient spatial intelligence transforming physical environments into interactive, reactive neural spaces with real-time volumetric rendering.
                </p>
              </div>

              <div className="relative z-10 pt-6 sm:pt-8 flex items-center justify-between border-t border-white/[0.06] mt-6 sm:mt-8">
                <span className="text-xs sm:text-sm font-medium text-white flex items-center gap-2 group-hover:text-blue-400 transition-colors">
                  <span>Experience FOZZIL ARK</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" />
                </span>
                <span className="text-xs font-mono text-zinc-500">ARK OS 3.1</span>
              </div>
            </motion.div>

            {/* Bento 2: MEDOS (1 Col) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              onClick={() => navigate("/medos")}
              className="col-span-1 relative rounded-[22px] sm:rounded-[40px] bg-gradient-to-b from-white/[0.06] to-white/[0.015] border border-white/[0.08] hover:border-blue-500/30 p-4 sm:p-8 md:p-10 overflow-hidden group cursor-pointer transition-colors duration-500 flex flex-col justify-between min-h-[280px] sm:min-h-[380px]"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-[90px] rounded-full pointer-events-none group-hover:bg-blue-500/20 transition-all duration-700" />

              <div className="relative z-10 space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-blue-400">
                    Healthcare OS
                  </span>
                  <span className="text-[10px] sm:text-xs font-mono text-zinc-500 uppercase tracking-widest">v2.1</span>
                </div>
                <h4 className="text-lg sm:text-3xl md:text-4xl font-semibold tracking-tight text-white group-hover:text-blue-400 transition-colors">
                  MEDOS
                </h4>
                <p className="text-xs sm:text-base text-zinc-300 font-light leading-relaxed line-clamp-3 sm:line-clamp-none">
                  1st AI Hospital Pharmacy Management OS. Eliminating medication dispensing errors through deterministic verification and automated inventory synchronization.
                </p>
              </div>

              <div className="relative z-10 pt-4 sm:pt-6 flex items-center justify-between border-t border-white/[0.06]">
                <span className="text-xs sm:text-sm font-medium text-white flex items-center gap-1.5 sm:gap-2 group-hover:text-blue-400 transition-colors">
                  <span>Explore MEDOS</span>
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1.5 transition-transform" />
                </span>
                <span className="text-[10px] sm:text-xs font-mono text-zinc-500">Clinical Active</span>
              </div>
            </motion.div>

            {/* Bento 3: ZYPHER AI (1 Col) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              onClick={() => navigate("/zypherai")}
              className="col-span-1 relative rounded-[22px] sm:rounded-[40px] bg-gradient-to-b from-white/[0.06] to-white/[0.015] border border-white/[0.08] hover:border-blue-500/30 p-4 sm:p-8 md:p-10 overflow-hidden group cursor-pointer transition-colors duration-500 flex flex-col justify-between min-h-[280px] sm:min-h-[380px]"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-[90px] rounded-full pointer-events-none group-hover:bg-blue-500/20 transition-all duration-700" />

              <div className="relative z-10 space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-blue-400">
                    Clinical Advisory
                  </span>
                  <span className="text-[10px] sm:text-xs font-mono text-zinc-500 uppercase tracking-widest">Autonomous</span>
                </div>
                <h4 className="text-lg sm:text-3xl md:text-4xl font-semibold tracking-tight text-white group-hover:text-blue-400 transition-colors">
                  ZYPHER AI
                </h4>
                <p className="text-xs sm:text-base text-zinc-300 font-light leading-relaxed line-clamp-3 sm:line-clamp-none">
                  Neuro-Clinical Advisory Engine. Transforming multifaceted diagnostic observations into clear, explainable, and verified medical recommendations.
                </p>
              </div>

              <div className="relative z-10 pt-4 sm:pt-6 flex items-center justify-between border-t border-white/[0.06]">
                <span className="text-xs sm:text-sm font-medium text-white flex items-center gap-1.5 sm:gap-2 group-hover:text-blue-400 transition-colors">
                  <span>Discover ZYPHER</span>
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1.5 transition-transform" />
                </span>
                <span className="text-[10px] sm:text-xs font-mono text-zinc-500">Neural Engine</span>
              </div>
            </motion.div>

            {/* Bento 4: FOZZIL Store & Hardware (2 Cols) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              onClick={() => navigate("/shop")}
              className="col-span-2 lg:col-span-2 relative rounded-[24px] sm:rounded-[40px] bg-gradient-to-b from-white/[0.06] to-white/[0.015] border border-white/[0.08] hover:border-blue-500/30 p-5 sm:p-10 md:p-12 overflow-hidden group cursor-pointer transition-colors duration-500 flex flex-col justify-between min-h-[300px] sm:min-h-[380px]"
            >
              <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-500/10 blur-[100px] rounded-full pointer-events-none group-hover:bg-blue-500/20 transition-all duration-700" />

              <div className="relative z-10 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-mono uppercase tracking-widest text-blue-400">
                    Ecosystem Provisioning
                  </span>
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
                    Hardware & Cloud
                  </span>
                </div>
                <h4 className="text-2xl sm:text-5xl font-semibold tracking-tight text-white group-hover:text-blue-400 transition-colors">
                  FOZZIL Store & Subscriptions
                </h4>
                <p className="text-sm sm:text-lg text-zinc-300 font-light leading-relaxed max-w-xl">
                  Deploy proprietary FOZZIL hardware terminals, edge computing nodes, and enterprise clinical intelligence tiers tailored for institutional workflows.
                </p>
              </div>

              <div className="relative z-10 pt-6 sm:pt-8 flex items-center justify-between border-t border-white/[0.06] mt-6 sm:mt-8">
                <span className="text-xs sm:text-sm font-medium text-white flex items-center gap-2 group-hover:text-blue-400 transition-colors">
                  <span>Visit FOZZIL Store</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" />
                </span>
                <span className="text-xs font-mono text-zinc-500">Official Store</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* 5. FOZZIL ID (Apple-Style Credential Card) */}
      <section className="py-24 md:py-40 px-6 bg-black border-t border-white/[0.06] relative overflow-hidden">
        {/* Ambient atmospheric glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-blue-600/5 blur-[140px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
          {/* Left Column: Typography & CTAs */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="flex-1 space-y-8 text-center lg:text-left"
          >
            <div className="space-y-4">
              <h3 className="text-4xl sm:text-6xl md:text-7xl font-semibold tracking-[-0.03em] leading-tight text-white">
                Your FOZZIL ID.
              </h3>
            </div>

            <p className="text-lg sm:text-xl text-zinc-400 font-light leading-relaxed max-w-xl mx-auto lg:mx-0">
              The master key to the entire FOZZIL ecosystem. Passkey-native biometric authentication, zero-knowledge cryptographic safeguards, and instantaneous synchronization across all your devices.
            </p>

            {/* Checklist */}
            <div className="space-y-3.5 max-w-lg mx-auto lg:mx-0 text-left">
              <div className="flex items-center gap-3 text-zinc-300 text-sm sm:text-base">
                <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" />
                <span>Password-free passkeys backed by hardware biometrics</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-300 text-sm sm:text-base">
                <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" />
                <span>Unified Single Sign-On across Store, Intelligence & Cloud Ecosystem</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-300 text-sm sm:text-base">
                <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" />
                <span>Zero-knowledge end-to-end encryption vault</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-4">
              <button
                onClick={() => navigate("/signup")}
                className="px-8 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition-all shadow-[0_0_30px_rgba(255,255,255,0.15)] hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
              >
                Create Your FOZZIL ID
              </button>
              <button
                onClick={() => navigate("/login")}
                className="px-7 py-3.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-white/20 text-white font-medium text-sm transition-all cursor-pointer"
              >
                Sign In to ID
              </button>
            </div>
          </motion.div>

          {/* Right Column: Apple-Style Holographic Access Pass */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="flex-1 flex justify-center w-full"
          >
            <motion.div
              whileHover={{ y: -8, rotateX: 4, rotateY: -4 }}
              transition={{ duration: 0.4 }}
              className="relative w-full max-w-[420px] aspect-[1.58/1] rounded-[28px] sm:rounded-[36px] bg-gradient-to-br from-[#0a0d17] via-[#05070d] to-[#020306] border border-white/[0.12] p-6 sm:p-8 flex flex-col justify-between shadow-[0_30px_90px_rgba(6,93,192,0.25)] overflow-hidden group"
            >
              {/* Dynamic specular light sweep */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.04] to-transparent pointer-events-none" />

              {/* Card Top Row */}
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10 rounded-xl overflow-hidden border border-white/10 bg-black flex items-center justify-center">
                    <img src="/logoicon.png" alt="FOZZIL" className="w-full h-full object-cover mix-blend-screen" />
                  </div>
                  <div className="text-sm font-semibold text-white tracking-wide">FOZZIL ID</div>
                </div>
              </div>

              {/* Microchip & Hologram strip */}
              <div className="relative z-10 flex items-center gap-4 my-auto">
                <div className="relative w-12 h-9 rounded-lg bg-gradient-to-br from-blue-400/20 via-[#065DC0]/15 to-blue-600/25 border border-blue-400/40 flex items-center justify-center">
                  <Cpu className="w-5 h-5 text-blue-300" />
                </div>
                <div className="flex-1 h-[1px] bg-gradient-to-r from-blue-500/40 via-blue-400/20 to-transparent" />
                <div className="text-[11px] font-mono text-zinc-400 tracking-widest">FIDO2 · BIOMETRIC</div>
              </div>

              {/* Card Bottom Row */}
              <div className="relative z-10 flex items-end justify-between pt-2">
                <div className="text-xs sm:text-sm font-semibold text-white tracking-wide font-mono">
                  JADEN ROY
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* 6. VARAEN & S.H.A.D.E. (Apple-Style Security Spotlight) */}
      <section className="py-28 md:py-48 px-6 bg-black border-t border-white/[0.06] relative overflow-hidden">
        {/* Ambient atmospheric lighting */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[550px] bg-[radial-gradient(ellipse_70%_50%_at_50%_50%,rgba(6,93,192,0.15),transparent_70%)] blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-5xl mx-auto text-center space-y-12">
          {/* Dual Official Security Badges */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center justify-center gap-6"
          >
            {/* Official VARAEN Metallic Logo */}
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-3xl overflow-hidden border border-[#065DC0]/40 shadow-2xl shadow-[#065DC0]/30 bg-[#07090e]">
              <img src="/assets/varaen-logo.jpg" alt="FOZZIL VARAEN" className="w-full h-full object-cover block" />
            </div>

            <div className="w-[1px] h-12 bg-white/10" />

            {/* Official S.H.A.D.E. Engine Logo */}
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02] flex items-center justify-center p-3">
              <img src="/S.H.A.D.E logo-wobg.png" alt="S.H.A.D.E. Defense Engine" className="w-full h-full object-contain" />
            </div>
          </motion.div>

          {/* Slogan */}
          <div className="space-y-4">
            <motion.h3
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-4xl sm:text-6xl md:text-8xl font-semibold tracking-[-0.03em] leading-tight text-white"
            >
              Your Intelligence. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-indigo-300">
                Unseen. Untouched.
              </span>
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-base sm:text-xl md:text-2xl text-zinc-400 font-light leading-relaxed max-w-3xl mx-auto"
            >
              Privacy is not an added feature — it is our foundational architecture. All FOZZIL websites and APIs are protected by <strong>VARAEN</strong>, with <strong>S.H.A.D.E.</strong> autonomously isolating hostile threats in real time.
            </motion.p>
          </div>

          {/* 3 Apple-Style Privacy Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5 sm:gap-5 text-left pt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="p-4 sm:p-7 rounded-2xl sm:rounded-3xl bg-white/[0.02] border border-white/[0.07] hover:border-blue-500/30 transition-colors"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center mb-3 sm:mb-4">
                <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <h4 className="text-sm sm:text-lg font-semibold text-white mb-1 sm:mb-2">Zero-Knowledge Compute</h4>
              <p className="text-[11px] sm:text-xs sm:text-sm text-zinc-400 leading-relaxed">
                Plaintext clinical and personal records are encrypted client-side. Even FOZZIL infrastructure cannot inspect your private data.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="p-4 sm:p-7 rounded-2xl sm:rounded-3xl bg-white/[0.02] border border-white/[0.07] hover:border-blue-500/30 transition-colors"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center mb-3 sm:mb-4">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <h4 className="text-sm sm:text-lg font-semibold text-white mb-1 sm:mb-2">S.H.A.D.E. Defense</h4>
              <p className="text-[11px] sm:text-xs sm:text-sm text-zinc-400 leading-relaxed">
                Autonomous threat neutralization operating inside VARAEN. Suspicious injection attacks are quarantined in milliseconds.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="col-span-2 md:col-span-1 p-4 sm:p-7 rounded-2xl sm:rounded-3xl bg-white/[0.02] border border-white/[0.07] hover:border-blue-500/30 transition-colors"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center mb-3 sm:mb-4">
                <Scale className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <h4 className="text-sm sm:text-lg font-semibold text-white mb-1 sm:mb-2">Ethical Intelligence</h4>
              <p className="text-[11px] sm:text-xs sm:text-sm text-zinc-400 leading-relaxed">
                No public model training on your private files or clinical records. Strict deterministic guardrails protect patient safety and confidentiality.
              </p>
            </motion.div>
          </div>

          {/* Privacy Link (Opens in new tab always) */}
          <div className="pt-4">
            <a
              href="/security-and-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2997ff] hover:text-sky-300 font-medium text-base inline-flex items-center gap-2 group cursor-pointer"
            >
              <span>Explore Privacy & Security at FOZZIL</span>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>


      {/* 7. FOZZIL RESEARCH & FRONTIER INITIATIVES */}
      <section className="py-24 md:py-36 bg-black px-6 border-t border-white/[0.06] relative overflow-hidden">
        {/* Subtle radial ambient blue glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,rgba(6,93,192,0.12),transparent_70%)] blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="inline-block text-xs font-mono font-semibold text-blue-400 tracking-[0.3em] uppercase mb-4">
                FOZZIL RESEARCH · FRONTIER LABS
              </span>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white mb-6">
                Pioneering the Boundaries <br className="hidden sm:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-blue-500">
                  of Autonomous Intelligence.
                </span>
              </h2>
              <p className="text-base sm:text-lg text-zinc-400 font-light leading-relaxed">
                Beyond conventional models. FOZZIL Research bridges theoretical breakthrough with real-world human progress — from automated workflow orchestration at global scale to open-horizon public engagement.
              </p>
            </motion.div>
          </div>

          {/* 3 Showcase Bento Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5 sm:gap-6 lg:gap-8 mb-14">
            
            {/* Card 1: AWM */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => navigate("/research/awm")}
              className="group relative bg-[#090b0e] border border-white/[0.08] hover:border-blue-500/40 rounded-[20px] sm:rounded-[28px] p-4 sm:p-7 md:p-8 flex flex-col justify-between transition-all duration-500 hover:shadow-[0_20px_50px_rgba(6,93,192,0.18)] cursor-pointer overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-36 h-36 bg-blue-500/10 blur-[50px] group-hover:bg-blue-500/20 transition-all pointer-events-none rounded-full" />
              
              <div>
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 group-hover:scale-105 transition-transform">
                    <FlaskConical className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-mono tracking-wider text-blue-400 uppercase">
                    AWM · FRAMEWORK
                  </span>
                </div>

                <h3 className="text-base sm:text-xl md:text-2xl font-semibold text-white tracking-tight mb-2 sm:mb-3 group-hover:text-blue-400 transition-colors">
                  Automated Workflow Management
                </h3>

                <p className="text-xs sm:text-sm text-zinc-400 font-light leading-relaxed mb-4 sm:mb-6 line-clamp-3 sm:line-clamp-none">
                  A framework for eliminating operational friction through intelligent orchestration, instant cross-continental synchronization, and automated task transitions.
                </p>
              </div>

              <div className="pt-3 sm:pt-4 border-t border-white/[0.06] flex items-center justify-between text-[11px] sm:text-xs font-medium text-zinc-300 group-hover:text-blue-400 transition-colors">
                <span>Read Research Paper</span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>

            {/* Card 2: AI Museum */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => navigate("/research/ai-museum")}
              className="group relative bg-[#090b0e] border border-white/[0.08] hover:border-blue-500/40 rounded-[20px] sm:rounded-[28px] p-4 sm:p-7 md:p-8 flex flex-col justify-between transition-all duration-500 hover:shadow-[0_20px_50px_rgba(6,93,192,0.18)] cursor-pointer overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-36 h-36 bg-blue-500/10 blur-[50px] group-hover:bg-blue-500/20 transition-all pointer-events-none rounded-full" />
              
              <div>
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 group-hover:scale-105 transition-transform">
                    <Compass className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-mono tracking-wider text-blue-400 uppercase">
                    PUBLIC ENGAGEMENT
                  </span>
                </div>

                <h3 className="text-base sm:text-xl md:text-2xl font-semibold text-white tracking-tight mb-2 sm:mb-3 group-hover:text-blue-400 transition-colors">
                  The AI Museum Initiative
                </h3>

                <p className="text-xs sm:text-sm text-zinc-400 font-light leading-relaxed mb-4 sm:mb-6 line-clamp-3 sm:line-clamp-none">
                  A new format for public engagement, demystifying neural systems and ethical governance through interactive volumetric exhibits and transparent discourse.
                </p>
              </div>

              <div className="pt-3 sm:pt-4 border-t border-white/[0.06] flex items-center justify-between text-[11px] sm:text-xs font-medium text-zinc-300 group-hover:text-blue-400 transition-colors">
                <span>Explore Initiative</span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>

            {/* Card 3: Company Story & Vision */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => navigate("/stories/company")}
              className="col-span-2 md:col-span-1 group relative bg-[#090b0e] border border-white/[0.08] hover:border-blue-500/40 rounded-[20px] sm:rounded-[28px] p-4 sm:p-7 md:p-8 flex flex-col justify-between transition-all duration-500 hover:shadow-[0_20px_50px_rgba(6,93,192,0.18)] cursor-pointer overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-36 h-36 bg-blue-500/10 blur-[50px] group-hover:bg-blue-500/20 transition-all pointer-events-none rounded-full" />
              
              <div>
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 group-hover:scale-105 transition-transform">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-mono tracking-wider text-blue-400 uppercase">
                    OUR JOURNEY · 2021
                  </span>
                </div>

                <h3 className="text-base sm:text-xl md:text-2xl font-semibold text-white tracking-tight mb-2 sm:mb-3 group-hover:text-blue-400 transition-colors">
                  The Origin & Evolution
                </h3>

                <p className="text-xs sm:text-sm text-zinc-400 font-light leading-relaxed mb-4 sm:mb-6 line-clamp-3 sm:line-clamp-none">
                  From a foundational aerospace mentorship to building an integrated ecosystem: discover how FOZZIL evolved into a home for adaptive, human-centric intelligence.
                </p>
              </div>

              <div className="pt-3 sm:pt-4 border-t border-white/[0.06] flex items-center justify-between text-[11px] sm:text-xs font-medium text-zinc-300 group-hover:text-blue-400 transition-colors">
                <span>Read The Company Story</span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>

          </div>

          {/* Bottom Action Deck */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
            <button
              onClick={() => navigate("/research/awm")}
              className="w-full sm:w-auto bg-white text-black px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-zinc-200 transition-all cursor-pointer shadow-[0_0_25px_rgba(255,255,255,0.15)] hover:scale-[1.02] active:scale-[0.98] inline-flex items-center justify-center gap-2"
            >
              <span>Explore Research Papers</span>
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/about")}
              className="w-full sm:w-auto px-7 py-3.5 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/20 text-white font-medium text-sm transition-all cursor-pointer hover:bg-white/[0.08]"
            >
              About FOZZIL
            </button>
          </div>

          <p className="text-center mt-10 text-[11px] font-mono text-zinc-500 tracking-[0.25em] uppercase">
            Future Oriented Zone-of Intelligent Life · Research & Exploration
          </p>
        </div>
      </section>


      {/* 8. GRAND FINALE / CONCLUSION CTA (Apple Cinematic Horizon) */}
      <section className="h-[70vh] md:h-[85vh] flex flex-col items-center justify-center text-center px-6 relative overflow-hidden bg-black border-t border-white/[0.06]">
        {/* Ambient bottom illumination */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1000px] h-[400px] bg-[radial-gradient(ellipse_70%_50%_at_50%_100%,rgba(6,93,192,0.18),transparent_70%)] blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto space-y-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-5xl sm:text-7xl md:text-9xl font-semibold tracking-[-0.04em] text-white leading-[0.92]"
          >
            Innovate <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-zinc-500">
              Your Future.
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="text-lg sm:text-2xl text-zinc-400 font-light max-w-2xl mx-auto leading-relaxed"
          >
            Experience intelligence built with absolute privacy, uncompromising speed, and unified zero-trust architecture.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center justify-center gap-4 pt-4"
          >
            <button
              onClick={() => window.open("https://taels.fozzil.in/app", "_blank", "noopener,noreferrer")}
              className="px-8 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition-all shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              Command T.A.E.L.S
            </button>
            <button
              onClick={() => navigate("/fozzil-ark")}
              className="px-7 py-3.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-white/20 text-white font-medium text-sm transition-all cursor-pointer"
            >
              Explore Products
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MainContent;
